/**
 * # dxfBridge.documentHandleCensus — the document-wide handle census (one truth)
 *
 * Every minter that must avoid ALIASING an existing handle of a document —
 * the editor store's import-merge collision-rename, the imported-drawing
 * export preparation (`dxfBridge.newRecordHandles`), the conformant
 * skeleton's seed — needs the same answer to "which handles does this
 * document already carry?". A hand-picked field list drifts (the store's list
 * once missed the name-keyed `linetypes` table, so a collision-rename mint
 * aliased an LTYPE record handle), so the partition of `drawingDocumentSchema`
 * into handle-bearing and handle-free top-level fields lives HERE, and a
 * census test proves the two lists cover every schema key.
 *
 * `collectHandlesDeep` (annotativeExport) is the leaf walker: every
 * `handle` / `*Handle` string, every `*Handles` array member and every raw
 * `{code: 5|105|1005}` group, lowercased.
 */
import { collectHandlesDeep } from "./dxfBridge.annotativeExport";
import type { DrawingDocument } from "./model";

/**
 * Top-level document fields that can carry a DXF handle (own or reference):
 * the typed tables / records / spaces and the verbatim raw group streams.
 */
export const DOCUMENT_HANDLE_FIELDS = [
  "spaces",
  "styleTables",
  "blocks",
  "blockRecords",
  "linetypes",
  "linetypeTableHeader",
  "symbolTableHeaders",
  "layouts",
  "layoutsBySpaceId",
  "viewportsBySpaceId",
  "xrefRegistry",
  "rawEntityGroups",
  "rawObjectGroups",
  "rawAssocArrayObjects",
  "rawTableGroups",
  "savedView",
  "rawPassthroughSections",
] as const satisfies ReadonlyArray<keyof DrawingDocument>;

/**
 * Top-level document fields that can never carry a DXF handle (scalars, names,
 * flags, diagnostics text) — deliberately NOT walked by the census.
 */
export const HANDLE_FREE_DOCUMENT_FIELDS = [
  "id",
  "title",
  "activeSpaceId",
  "provenance",
  "insunits",
  "pdmode",
  "pdsize",
  "insertionBase",
  "ltscale",
  "celtscale",
  "psltscale",
  "msltscale",
  "osmode",
  "mirrtext",
  "fillmode",
  "cecolor",
  "cannoscale",
  "cannoscaleName",
  "annotationScales",
  "annoAllVisible",
  "annoAutoScale",
  "annoMonitor",
  "importDiagnostics",
] as const satisfies ReadonlyArray<keyof DrawingDocument>;

/**
 * Harvest every handle (lowercased) the document carries into `out` — own
 * handles (entity ids included) AND references, over every
 * {@link DOCUMENT_HANDLE_FIELDS} field.
 * Over-approximating with references is deliberate: a minter that avoids a
 * referenced-but-unemitted handle only mints higher, never a collision.
 */
export function collectDocumentHandlesDeep(
  document: DrawingDocument,
  out: Set<string>,
  options: {
    /**
     * Also count every emitted entity `id` (the entity's own group-5).
     * Default `true`; a caller that walks the entity ids itself (the store's
     * import-merge collision-rename, which must NOT see the imported batch's
     * own ids as collisions) passes `false`.
     */
    entityIds?: boolean;
  } = {},
): void {
  for (const field of DOCUMENT_HANDLE_FIELDS) {
    if (field === "rawPassthroughSections") {
      // The HEADER `9 $HANDSEED / 5 <seed>` pair: the seed's `5` is NOT an
      // object handle (it is the next FREE one) — counting it would push every
      // minter one past the seed and churn `$HANDSEED` on each save.
      for (const section of document.rawPassthroughSections ?? []) {
        collectHandlesDeep(
          section.name === "HEADER" ? withoutHandSeedGroup(section.groups) : section.groups,
          out,
        );
      }
      continue;
    }
    collectHandlesDeep(document[field], out);
  }
  // An entity's `id` IS its DXF group-5 (the export forwards it as
  // `common.handle`); `collectHandlesDeep` reads only `handle` / `*Handle`
  // keys, so the ids are added here — a minter that skipped them could hand
  // out a handle an entity already owns.
  if (options.entityIds === false) return;
  for (const space of document.spaces) {
    for (const entity of space.entities) noteId(entity.id, out);
  }
  for (const block of Object.values(document.blocks ?? {})) {
    for (const child of block.entities) noteId(child.id, out);
  }
}

const HEX_HANDLE_RE = /^[0-9A-Fa-f]+$/;

function noteId(id: string, out: Set<string>): void {
  if (HEX_HANDLE_RE.test(id)) out.add(id.toLowerCase());
}

/** The HEADER groups minus the `5 <seed>` value that follows `9 $HANDSEED`. */
function withoutHandSeedGroup<T extends { code: number; value: string }>(
  groups: readonly T[],
): T[] {
  const out: T[] = [];
  let afterSeedName = false;
  for (const g of groups) {
    const isSeedValue = afterSeedName && g.code === 5;
    afterSeedName = g.code === 9 && g.value.trim() === "$HANDSEED";
    if (!isSeedValue) out.push(g);
  }
  return out;
}
